/**
 * 팀 파일 (Resources) — Ops › Brief › Team files 와 워크보드 Resources 가 같이 쓴다.
 * 서버 모양: { id, name, kind: 'file'|'link', fileName, size, url, files, linkType, group, roles, pinned, updatedAt }
 *   파일이면 url = 받기 주소, 링크면 그 링크. Project brief(pinned)에 파일이 없으면 url 이 비어 있다.
 *   files = 한 항목의 파일 여러 개 [{ key, fileName, size, url }] (예: tokens.css + 미리보기 jpg). 첫 파일이 fileName · size · url
 */

import { isWebUrl } from './handoffSlots';

export const GROUPS = [
  { key: 'company', label: 'Background' },
  { key: 'working', label: 'Data and guides' },
  { key: 'templates', label: 'Templates' },
];
export const groupLabel = (key) => GROUPS.find((g) => g.key === key)?.label ?? '';

/** 파일럿은 PM · Designer · Developer (Marketer 제외). All roles = 이 3개 */
export const ROLES = ['PM', 'Designer', 'Developer'];

/** 워크보드 직군 → 파일 역할 */
export const ROLE_OF_JOB = {
  planner: 'PM',
  designer: 'Designer',
  developer: 'Developer',
};

export const LINK_TYPES = [
  { key: 'Figma', placeholder: 'https://www.figma.com/file/…' },
  { key: 'GitHub', placeholder: 'https://github.com/…' },
  { key: 'Notion', placeholder: 'https://www.notion.so/…' },
  { key: 'Web', placeholder: 'https://…' },
];

export const FILE_ACCEPT = '.json,.md,.png,.jpg,.jpeg,.svg,.webp,.gif,.pdf,.html,.htm,.css,.zip,.fig';
export const FILE_TYPES_LABEL = 'JSON, Markdown, image, PDF, HTML, CSS, ZIP or Figma (.fig)';
export const MAX_FILE_BYTES = 50 * 1024 * 1024;
/** 한 항목에 올릴 수 있는 파일 수 (서버와 같다) */
export const MAX_FILES = 5;

/** 확장자로 형식 — json→JSON, md→Markdown, 그림→Image, pdf→PDF, html→HTML, css→CSS, zip→ZIP, fig→Figma */
export const typeOf = (name = '') => {
  const ext = name.split('.').pop()?.toLowerCase();
  if (ext === 'json') return 'JSON';
  if (ext === 'md') return 'Markdown';
  if (['png', 'jpg', 'jpeg', 'svg', 'webp', 'gif'].includes(ext)) return 'Image';
  if (ext === 'pdf') return 'PDF';
  if (ext === 'html' || ext === 'htm') return 'HTML';
  if (ext === 'css') return 'CSS';
  if (ext === 'zip') return 'ZIP';
  if (ext === 'fig') return 'Figma';
  return null;
};

/** 항목의 파일들 — 예전 응답(files 없음)은 fileName · size · url 한 개 */
export const filesOf = (f) => {
  if (f?.kind !== 'file') return [];
  if (f.files?.length) return f.files;
  return f.fileName ? [{ key: null, fileName: f.fileName, size: f.size, url: f.url }] : [];
};

/** 1.2 MB · 18 KB */
export const sizeLabel = (bytes) => {
  if (!bytes) return '';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

export const hostOf = (url) => {
  try {
    return new URL(url).host.replace(/^www\./, '');
  } catch {
    return '';
  }
};

/** URL 호스트로 링크 종류 추천 — 사용자가 고른 값이 우선 */
export const guessLink = (url) => {
  const host = hostOf(url);
  if (/(^|\.)figma\.com$/.test(host)) return 'Figma';
  if (/(^|\.)github\.com$/.test(host)) return 'GitHub';
  if (/(^|\.)notion\.(so|site|com)$/.test(host)) return 'Notion';
  return 'Web';
};

/** http(s) 전체 주소 (localhost · 포트 포함) — 슬롯 검사와 같은 규칙 */
export const isLinkUrl = isWebUrl;

/** Google Drive 링크는 받지 않는다 (Docs · Notion · Figma 로 공유) */
export const DRIVE_MSG =
  'Google Drive links can’t be used. Share it as a Google Docs, Notion, or Figma link instead.';
export const isDrive = (url) => /(^|\.)drive\.google\.com$/.test(hostOf(url));

/** 파일 이름에서 확장자를 뺀 이름 (Name 자동 채우기) */
export const baseName = (name = '') => name.replace(/\.[^.]+$/, '');

/** "PDF · 1.2 MB" · "CSS + Image · 2 files · 1.4 MB" · "Figma link · figma.com" */
export const metaOf = (f) => {
  if (f.kind === 'link') return `${f.linkType} link · ${hostOf(f.url)}`;
  const list = filesOf(f);
  if (list.length <= 1) return [typeOf(f.fileName), sizeLabel(f.size)].filter(Boolean).join(' · ');
  const types = [...new Set(list.map((x) => typeOf(x.fileName)).filter(Boolean))].join(' + ');
  const total = list.reduce((sum, x) => sum + (x.size ?? 0), 0);
  return [types, `${list.length} files`, sizeLabel(total)].filter(Boolean).join(' · ');
};

/** 파일럿 역할만 (예전에 고른 Marketer 는 뺀다) */
export const pilotRoles = (roles = []) => ROLES.filter((r) => roles.includes(r));
export const isAllRoles = (roles = []) => ROLES.every((r) => roles.includes(r));
export const rolesLabel = (roles = []) =>
  isAllRoles(roles) ? 'All roles' : pilotRoles(roles).join(', ');

/** "Oct 16, 09:40" (KST) */
export const updatedLabel = (iso) =>
  iso
    ? new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Seoul',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }).format(new Date(iso))
    : '';
